import * as React from 'react';
import { useCallbackObservable } from 'react-use-observable';

import { App, Button, Checkbox, Modal, Typography } from 'antd';

import CoProducerClass from 'classes/CoProducer';
import CustomMaskedInput from 'components/MaskedInput';
import { finalize } from 'rxjs/operators';
import { sendEventToGA } from 'v2/helpers/analytics';
import productService from 'v2/services/product';

type SolidaritySquadModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  productId: number;
};

const PROJECT_ELEVAR_ID = 75879698;
const DEFAULT_COMMISSION_VALUE = 5;
const GA_CATEGORY = 'SS Modal - Projeto Elevar';

enum CloseReason {
  NOT_NOW_BUTTON,
  CLOSE_BUTTON
}

function SolidaritySquadModal({ isOpen, handleClose: handleCloseProp, productId }: SolidaritySquadModalProps) {
  const { message } = App.useApp();
  const [updateCoproducers] = useCallbackObservable((productId, coproducers) => {
    setIsAdding(true);
    return productService.updateCoproducers(productId, coproducers).pipe(
      finalize(() => {
        setIsAdding(false);
        message.success('Doação automática configurada com sucesso');
        handleClose();
      })
    );
  }, []);

  const [commissionValue, setCommissionValue] = React.useState(DEFAULT_COMMISSION_VALUE);
  const [commissionError, setCommissionError] = React.useState('');
  const [isTermsAccepted, setIsTermsAccepted] = React.useState(false);
  const [isAdding, setIsAdding] = React.useState(false);

  function validateCommissionValue(value: number) {
    if (value < 0.01) setCommissionError('O valor de porcentagem tem que ser no mínimo 0,01');
    else setCommissionError(null);
  }

  function handleCommissionValueChange(value: string) {
    const formattedValue = Number(value.replace(',', '.'));
    validateCommissionValue(formattedValue);
    setCommissionValue(formattedValue);
  }

  function handleTermsChange(e: any) {
    setIsTermsAccepted(e.target.checked);
  }

  function createElevarAsCoProducer() {
    return new CoProducerClass({
      new: true,
      pending: false,
      business_name: 'Instituto Elevar',
      email: 'contato@projetoelevar.com.br',
      comission_type: 'P',
      comission_value: commissionValue,
      customer_id: PROJECT_ELEVAR_ID
    });
  }

  function removeCommissionFromProducer(coproducers: any[]) {
    return coproducers.map(coproducer => {
      if (coproducer.is_producer) coproducer.comission_value -= commissionValue;
      return coproducer;
    });
  }

  function handleSubmit(productId: number) {
    productService.getCoproducers(productId).subscribe(coproducers => {
      const updatedCoproducers = removeCommissionFromProducer(coproducers.active);
      updateCoproducers(productId, [...updatedCoproducers, createElevarAsCoProducer()]);
    });
    sendEventToGA({ category: GA_CATEGORY, action: 'Save Donation' });
  }

  function handleClose(closeReason?: CloseReason) {
    handleCloseProp();
    if (closeReason === CloseReason.CLOSE_BUTTON) sendEventToGA({ category: GA_CATEGORY, action: 'Close' });
    if (closeReason === CloseReason.NOT_NOW_BUTTON) sendEventToGA({ category: GA_CATEGORY, action: 'Not Now' });
  }

  return (
    <Modal
      open={isOpen}
      onCancel={() => handleClose(CloseReason.CLOSE_BUTTON)}
      width={1000}
      title='Faça a diferença e transforme vidas!'
      footer={
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button type='text' onClick={() => handleClose(CloseReason.NOT_NOW_BUTTON)}>
            Não quero contribuir
          </Button>
          <Button
            type='primary'
            disabled={!!commissionError || !isTermsAccepted || isAdding}
            loading={isAdding}
            onClick={() => handleSubmit(productId)}
          >
            Fazer a diferença
          </Button>
        </div>
      }
    >
      <div className='flex flex-col gap-4 md:flex-row'>
        <div className='hidden overflow-hidden rounded-(--uizz-antd-border-radius-lg) md:block md:w-2/5'>
          <img className='size-full object-cover object-center' src='/img/modal-elevar.png' alt='Projeto elevar' />
        </div>

        <div className='flex min-w-0 flex-1 flex-col gap-4'>
          <Typography.Text strong>Junte-se ao Instituto Elevar, uma organização de Assistência Social</Typography.Text>
          <Typography.Paragraph className='mb-0' type='secondary'>
            Transformamos vidas por meio da educação, tecnologia, esporte, arte, cultura, qualificação profissional e
            empreendedorismo.
          </Typography.Paragraph>

          <div className='rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
            <CustomMaskedInput
              id='comission_value'
              name='comission_value'
              mask='percent'
              label='Porcentagem da contribuição'
              helperText={commissionError}
              error={!!commissionError}
              startAdornment='%'
              fullWidth
              disableUnderline
              onChange={handleCommissionValueChange}
              value={String(commissionValue)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ min: 0.01 }}
            />
          </div>

          <Checkbox checked={isTermsAccepted} onChange={handleTermsChange}>
            {`Concordo em contribuir com ${commissionValue}% sobre cada venda deste produto para o Instituto Elevar.`}
          </Checkbox>
        </div>
      </div>
    </Modal>
  );
}

export { SolidaritySquadModal };
